import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { gql } from '@apollo/client';

import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  STORE_BADGE_HEIGHT,
  STORE_BADGE_WIDTH
} from '@lib/constants';
import { client, getPostSEOBySlug } from 'apollo-client';
import { getYoastHeadSEO } from '@lib/utils';
import { BodyRegular } from '@components/atoms/typography';
import Title from '@components/title';
import WideLayout from '@components/layout/wide';

// The QR encodes the /app device-aware redirect, so one code serves both
// stores. The source PNG is 592px so it stays crisp at 2x.
const QR_RENDERED_SIZE_PX = 96;

const FplMatchCentre = ({ pageData }) => {
  // The second argument is the route path, not the WordPress slug - the
  // slug produced a malformed canonical/og:url by string concatenation.
  const yoastHead = getYoastHeadSEO(
    pageData?.seo?.fullHead,
    '/fpl-match-centre'
  );

  return (
    <div className="matchcentre">
      <Head>
        {/* Yoast supplies the title; a second manual one shipped duplicate
            title tags. */}
        {yoastHead}
      </Head>
      <Title>{'FPL Match Centre'}</Title>
      <WideLayout content={false}>
        <div className="container mx-auto flex justify-center px-5 py-10 md:px-16 md:py-[72px]">
          <div className="flex w-full max-w-[640px] flex-col items-center gap-5 rounded-2xl border border-neutral-200 px-5 py-7 text-center md:gap-6 md:px-12 md:pb-11 md:pt-12">
            <span className="inline-flex items-center gap-[7px] rounded-full bg-primary-50 px-3.5 py-[5px]">
              <span
                className="h-[7px] w-[7px] flex-shrink-0 rounded-full bg-primary-600"
                aria-hidden
              />
              <span className="font-open-sans text-[11px] font-bold uppercase tracking-[0.12em] text-primary-700 md:text-xs">
                Now in the app
              </span>
            </span>

            <h1 className="font-sans text-[26px] font-bold leading-[1.25] text-neutral-800 [text-wrap:balance] md:text-4xl md:leading-[44px]">
              The FPL Match Centre has moved
            </h1>

            <BodyRegular
              textColor="text-neutral-600"
              className="max-w-[480px] text-[15px] [text-wrap:pretty] md:text-base"
            >
              Live points, bonus and rank movement are now part of Live mode in
              the Fantasy Football Hub app.
              <span className="hidden md:inline">
                {' '}
                Download it to keep following your team as matches play out.
              </span>
            </BodyRegular>

            <div className="flex w-full flex-col items-center gap-2.5 pt-1 md:w-auto md:flex-row md:flex-wrap md:justify-center md:gap-3">
              <Link
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <Image
                  src="/images/app-store-badge.png"
                  alt="Download on the App Store"
                  height={STORE_BADGE_HEIGHT}
                  width={STORE_BADGE_WIDTH}
                />
              </Link>
              <Link
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <Image
                  src="/images/google-play-badge.svg"
                  alt="Get it on Google Play"
                  height={STORE_BADGE_HEIGHT}
                  width={STORE_BADGE_WIDTH}
                />
              </Link>
            </div>

            <div className="mt-1 hidden w-full items-center gap-4 border-t border-neutral-200 pt-5 text-left md:flex">
              <Image
                src="/images/qr-app-download.png"
                alt="QR code linking to fantasyfootballhub.co.uk/app"
                height={QR_RENDERED_SIZE_PX}
                width={QR_RENDERED_SIZE_PX}
                className="flex-shrink-0 rounded-md"
              />
              <div className="flex flex-col gap-[3px]">
                <span className="font-open-sans text-sm font-bold text-neutral-800">
                  On a laptop? Scan to install.
                </span>
                <span className="font-open-sans text-[13px] leading-[1.5] text-neutral-600">
                  One code for both stores - it sends your phone to the right
                  one.
                </span>
              </div>
            </div>
          </div>
        </div>
      </WideLayout>
    </div>
  );
};

export default FplMatchCentre;

export async function getStaticProps() {
  const { data } = await client.query({
    query: gql`
      ${getPostSEOBySlug('fantasy-football-hub-fpl-match-centre')}
    `
  });

  return {
    props: {
      pageData: data?.postBy
    },
    revalidate: 3000
  };
}
